import { IconLightLow } from '@picsart/design-system/foundation/icons/IconLightLow';
import type { ElementType } from 'react';
import { IconDiamond } from '@picsart/design-system/foundation/icons/IconDiamond';
import { IconHSL } from '@picsart/design-system/foundation/icons/IconHSL';
import { IconSaturation } from '@picsart/design-system/foundation/icons/IconSaturation';
import { SliderTypes } from '@picsart/design-system/components/Slider';
import type { warp_fx } from '@picsart/video-sdk';

import { getAdjustDescriptor } from 'pages/Batch/utils/process/adjustProcess';

export const ADJUST_EFFECT_ID = 'adjust_default_with_hsl';

export const HSL_SLOTS_COUNT = 8;

export const HSL_PALETTE_COLORS = [
  '#ff5556', // Red
  '#ffc445', // Orange
  '#f9e64b', // Yellow
  '#50ca6d', // Green
  '#5fe2eb', // Cyan
  '#4843fa', // Blue
  '#b850da', // Purple
  '#f34ea9', // Pink
];

export interface IAdjustParam {
  name: string;
  label: string;
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  sliderType?: SliderTypes;
}

export interface IAdjustGroup {
  id: string;
  label: string;
  isHsl?: boolean;
  params: IAdjustParam[];
  icon: ElementType;
}

export interface GroupDefinition {
  id: string;
  label: string;
  isHsl?: boolean;
  paramNames: string[];
  icon: ElementType;
}

const SLIDER_TYPE_OVERRIDES: Record<string, SliderTypes> = {
  clarity: SliderTypes.Continuous,
};

export const ADJUST_GROUP_DEFINITIONS: GroupDefinition[] = [
  {
    id: 'light',
    label: 'Light',
    paramNames: ['brightness', 'contrast', 'shadows', 'highlights'],
    icon: IconLightLow,
  },
  {
    id: 'colors',
    label: 'Colors',
    paramNames: ['saturation', 'hue', 'temperature'],
    icon: IconSaturation,
  },
  {
    id: 'details',
    label: 'Details',
    paramNames: ['clarity'],
    icon: IconDiamond,
  },
  {
    id: 'hsl',
    label: 'HSL',
    isHsl: true,
    paramNames: [
      'isHslUsed',
      ...Array.from({ length: HSL_SLOTS_COUNT }, (_, i) => `hue${i}`),
      ...Array.from({ length: HSL_SLOTS_COUNT }, (_, i) => `lightness${i}`),
      ...Array.from({ length: HSL_SLOTS_COUNT }, (_, i) => `saturation${i}`),
    ],
    icon: IconHSL,
  },
];

function getDisplayName(paramName: string): string {
  if (paramName.startsWith('hue')) return 'Hue';
  if (paramName.startsWith('saturation')) return 'Saturation';
  if (paramName.startsWith('lightness')) return 'Lightness';
  return paramName.charAt(0).toUpperCase() + paramName.slice(1);
}

function buildParamFromDescriptor(paramName: string, descriptorParam: warp_fx.parameters.ParameterDescriptor): IAdjustParam {
  const json = descriptorParam.json as unknown as { value: number; min_max: [number, number]; step?: number };
  return {
    name: paramName,
    label: getDisplayName(paramName),
    min: json.min_max[0],
    max: json.min_max[1],
    step: json.step ?? 1,
    defaultValue: json.value,
    ...(SLIDER_TYPE_OVERRIDES[paramName] ? { sliderType: SLIDER_TYPE_OVERRIDES[paramName] } : { sliderType: SliderTypes.Centered }),
  };
}

let cachedGroups: IAdjustGroup[] | null = null;

export async function fetchAdjustGroups(): Promise<IAdjustGroup[]> {
  if (cachedGroups) return cachedGroups;

  const descriptor = await getAdjustDescriptor();
  const { parameters } = descriptor;

  const groups: IAdjustGroup[] = ADJUST_GROUP_DEFINITIONS.map(def => {
    const params: IAdjustParam[] = def.paramNames
      .map(name => {
        const descriptorParam = parameters.find((p: warp_fx.parameters.ParameterDescriptor) => p.name === name);
        if (!descriptorParam) return null;
        return buildParamFromDescriptor(name, descriptorParam);
      })
      .filter((p): p is IAdjustParam => p !== null);

    return {
      id: def.id,
      label: def.label,
      ...(def.isHsl && { isHsl: true }),
      params,
      icon: def.icon,
    };
  });

  cachedGroups = groups;
  return groups;
}

export function getDefaultAdjustParams(groups: IAdjustGroup[]): Record<string, number> {
  return groups.flatMap(group => group.params).reduce<Record<string, number>>((defaults, param) => ({ ...defaults, [param.name]: param.defaultValue }), {});
}
